import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useConvexAuth } from "convex/react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { useEffect } from "react";
import type { TemplateId } from "../../../convex/templateTypes";
import { TEMPLATE_IDS } from "../../../convex/templateTypes";
import {
  clearBuilderFlowState,
  clearLegacyPendingSiteState,
  OWNERSHIP_TOKEN_KEY,
} from "../../lib/builderFlowStorage";

import Header from "../../components/Header";
import SignInCard from "../../components/auth/SignInCard";

export const Route = createFileRoute("/auth/signin")({
  component: SignInPage,
  validateSearch: (search: Record<string, unknown>) => {
    const flow =
      search.flow === "signin" || search.flow === "signup"
        ? search.flow
        : undefined;
    const entry =
      search.entry === "cta" || search.entry === "template"
        ? search.entry
        : undefined;
    const template =
      typeof search.template === "string" &&
      (TEMPLATE_IDS as readonly string[]).includes(search.template)
        ? (search.template as TemplateId)
        : undefined;

    return { entry, flow, template };
  },
});

function SignInPage() {
  const { isAuthenticated } = useConvexAuth();
  const navigate = useNavigate();
  const claimSite = useMutation(api.weddingSites.claimSite);
  const search = Route.useSearch();
  const mySites = useQuery(
    api.weddingSites.getMySites,
    isAuthenticated ? {} : "skip",
  );

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    if (search.entry === "template" && search.template) {
      clearBuilderFlowState();
      navigate({
        search: { step: "details", template: search.template },
        to: "/create",
      });
      return;
    }

    if (search.entry === "cta") {
      clearBuilderFlowState();
      navigate({
        search: { step: undefined, template: undefined },
        to: "/create",
      });
      return;
    }

    const token = localStorage.getItem(OWNERSHIP_TOKEN_KEY);

    if (token) {
      claimSite({ ownershipToken: token })
        .then((result) => {
          clearLegacyPendingSiteState();
          navigate({ params: { slug: result.slug }, to: "/admin/$slug" });
        })
        .catch(() => {
          clearLegacyPendingSiteState();
        });
      return;
    }

    if (mySites === undefined) {
      return;
    }

    if (mySites.length > 0) {
      navigate({ params: { slug: mySites[0].slug }, to: "/admin/$slug" });
      return;
    }

    clearBuilderFlowState();
    navigate({
      search: { step: undefined, template: undefined },
      to: "/create",
    });
  }, [
    claimSite,
    isAuthenticated,
    mySites,
    navigate,
    search.entry,
    search.template,
  ]);

  return (
    <div className="min-h-screen bg-[var(--color-page)]">
      <Header />
      <main className="flex items-center justify-center px-4 py-16">
        <SignInCard
          initialMode="magic-link"
          initialStep="signUp"
          key={`${search.flow ?? "signin"}-${search.entry ?? "default"}-${search.template ?? "none"}`}
        />
      </main>
    </div>
  );
}
